import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { RefreshCw } from 'lucide-react';
import { AcaoPrimaria } from '../../componentes/acao-primaria';
import { Botao, Cartao, Indicador, Selo, type CorSelo } from '../../componentes/base';
import { Estado } from '../../componentes/estados';
import { get, textoDeErroGravado } from '../../nucleo/api';
import { dataHora, numero, relativo } from '../../nucleo/formato';
import { rotuloJob, rotuloValor } from '../../nucleo/rotulos';
import type { SaudeNucleo } from './tipos';
import { TextoCortado } from '../../componentes/texto-cortado';

export const COR_ESTADO_JOB: Record<string, CorSelo> = { pendente: 'azul', rodando: 'ciano', concluido: 'verde', erro: 'perigo', enviado: 'ciano' };

export function useSaudeNucleo() {
  return useQuery({ queryKey: ['/nucleo/saude'], queryFn: () => get<SaudeNucleo>('/nucleo/saude'), refetchInterval: 30_000 });
}

export const rotuloEstado = (t: (k: string, o?: Record<string, unknown>) => string, e: string) => t(`estadoJob.${e}`, { defaultValue: rotuloValor('estado', e).toLowerCase() });

const total = (linhas: { estado: string; total: number }[], estado: string) => linhas.find((l) => l.estado === estado)?.total ?? 0;

/** Fase 2 (W31, 0.8): os 7 primeiros caracteres do SHA (o tamanho que o git mostra), com o SHA inteiro no title e para o leitor de tela. */
export const shaCurto = (sha: string) => sha.slice(0, 7);

function ShaCurto({ sha }: { sha: string }) {
  return (
    <span title={sha} data-sha={sha}>
      <span aria-hidden>{shaCurto(sha)}</span>
      <span className="sr-only">{sha}</span>
    </span>
  );
}

/** M12.1: saúde (PostgreSQL, Redis, worker, versão no ar), jobs das últimas 24 horas e erros recentes. */
export function Painel() {
  const { t } = useTranslation('admin');
  const q = useSaudeNucleo();
  return (
    <>
      <AcaoPrimaria>
        <Botao icone={<RefreshCw className="size-4" />} carregando={q.isFetching} onClick={() => q.refetch()}>
          {t('painel.atualizar')}
        </Botao>
      </AcaoPrimaria>
      <Estado consulta={q}>
        {(d) => {
          const jobs24 = d.jobs_24h.reduce((a, j) => a + j.total, 0);
          const semErros = d.erros_recentes.length === 0 && d.webhooks_falhos.length === 0;
          return (
            <div className="flex flex-col gap-5">
              <section aria-label={t('painel.titulo')} className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
                <Indicador rotulo={t('painel.postgres')} valor={d.postgres.ok ? t('painel.online') : t('painel.fora')} cor={d.postgres.ok ? 'verde' : 'perigo'} detalhe={d.postgres.versao} />
                <Indicador rotulo={t('painel.redis')} valor={d.redis.ok ? t('painel.online') : t('painel.fora')} cor={d.redis.ok ? 'verde' : 'perigo'} />
                <Indicador
                  rotulo={t('painel.worker')}
                  valor={d.worker.ok ? t('painel.ativo') : t('painel.parado')}
                  cor={d.worker.ok ? 'verde' : 'perigo'}
                  detalhe={d.worker.ultimo_sinal ? t('painel.ultimoSinal', { quando: relativo(d.worker.ultimo_sinal) }) : t('painel.semSinal')}
                />
                {/* Fase 2 (W31, 0.8): o SHA inteiro (40 caracteres) estourava o card; aparecem 7, com o inteiro no title e para o leitor. */}
                <Indicador
                  rotulo={t('painel.versao')}
                  valor={<ShaCurto sha={d.versao} />}
                  detalhe={
                    d.worker.sha && d.worker.sha !== d.versao ? (
                      <>
                        {t('painel.worker')} <ShaCurto sha={d.worker.sha} />
                      </>
                    ) : undefined
                  }
                />
              </section>

              <Cartao titulo={t('painel.jobs24h')}>
                {jobs24 === 0 ? (
                  <p className="text-sm text-tinta-3">{t('painel.semJobs')}</p>
                ) : (
                  <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                    {(['pendente', 'rodando', 'concluido', 'erro'] as const).map((e) => (
                      <Indicador key={e} rotulo={t(`painel.jobsEstado.${e}`)} valor={numero(total(d.jobs_24h, e))} cor={e === 'erro' && total(d.jobs_24h, e) > 0 ? 'perigo' : 'tinta-1'} />
                    ))}
                  </div>
                )}
                {d.fila_24h.length > 0 && (
                  <p className="mt-3 flex flex-wrap items-center gap-2 text-xs text-tinta-3">
                    {t('painel.fila')}:
                    {d.fila_24h.map((f) => (
                      <Selo key={f.estado} cor={COR_ESTADO_JOB[f.estado] ?? 'neutro'}>
                        {rotuloEstado(t, f.estado)} {numero(f.total)}
                      </Selo>
                    ))}
                  </p>
                )}
              </Cartao>

              <Cartao titulo={t('painel.erros')}>
                {semErros ? (
                  <p className="text-sm text-tinta-3">{t('painel.semErros')}</p>
                ) : (
                  <div className="grid gap-5 lg:grid-cols-2">
                    <div className="min-w-0">
                      <h3 className="kicker mb-2">{t('painel.errosJobs')}</h3>
                      <ul className="divide-y divide-borda">
                        {d.erros_recentes.map((e) => (
                          <li key={e.id} className="flex min-w-0 flex-col gap-1 py-2 text-sm">
                            <span className="flex flex-wrap items-center justify-between gap-2">
                              <span className="truncate font-medium" title={e.tipo}>{rotuloJob(e.tipo)}</span>
                              <span className="numero text-xs text-tinta-3">{dataHora(e.created_at)}</span>
                            </span>
                            <TextoCortado linhas={2} className="text-xs break-words text-perigo">{textoDeErroGravado(e.erro)}</TextoCortado>
                            <span className="text-xs text-tinta-3">{t('painel.tentativas', { count: e.tentativas })}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="min-w-0">
                      <h3 className="kicker mb-2">{t('painel.errosWebhooks')}</h3>
                      <ul className="divide-y divide-borda">
                        {d.webhooks_falhos.map((w) => (
                          <li key={w.id} className="flex min-w-0 flex-col gap-1 py-2 text-sm">
                            <span className="flex flex-wrap items-center justify-between gap-2">
                              <span className="numero truncate font-medium" title={w.evento}>{w.evento}</span>
                              <span className="numero text-xs text-tinta-3">{dataHora(w.updated_at)}</span>
                            </span>
                            <span className="truncate text-xs text-tinta-3" title={w.url}>{w.url}</span>
                            <span className="text-xs text-perigo">
                              {w.status_http ? `HTTP ${w.status_http}` : ''} {t('painel.tentativas', { count: w.tentativa })}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
              </Cartao>
            </div>
          );
        }}
      </Estado>
    </>
  );
}
